import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/shared/ui/dialog";
import { Button } from "@/shared/ui/button";
import { Loader2 } from "lucide-react";
import type {
  PropertyPlan,
  DeliveryState,
} from "@/features/properties/services/deliveryStateRecompute";

const COLUMN_LABELS: Record<keyof DeliveryState, string> = {
  survey_completed: "Survey completed",
  report_completed: "Report completed",
  vr_tours_completed: "VR tours completed",
  files_documents_completed: "Files & documents completed",
  delivery_status: "Delivery status",
};

function formatValue(value: boolean | string): string {
  return typeof value === "boolean" ? (value ? "true" : "false") : value;
}

type RecomputeReviewDialogProps = {
  open: boolean;
  plans: PropertyPlan[];
  applying: boolean;
  onConfirm: () => void;
  onCancel: () => void;
};

export function RecomputeReviewDialog({
  open,
  plans,
  applying,
  onConfirm,
  onCancel,
}: RecomputeReviewDialogProps) {
  const changedPlans = plans.filter((p) => p.changed);
  const isBulk = plans.length > 1;
  const nothingToDo = changedPlans.length === 0;

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        // Allow dismiss while applying only in the nothing-to-do state, so the
        // dialog can never become unclosable.
        if (!o && (!applying || nothingToDo)) onCancel();
      }}
    >
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Review status changes</DialogTitle>
          <DialogDescription>
            {nothingToDo
              ? "Everything is already up to date — no changes to apply."
              : isBulk
              ? `${changedPlans.length} of ${plans.length} properties will be updated.`
              : "The following columns will be updated."}
          </DialogDescription>
        </DialogHeader>

        {!nothingToDo && (
          <div className="max-h-[50vh] overflow-y-auto space-y-3">
            {changedPlans.map((plan) => (
              <div key={plan.id} className="rounded-lg border border-border/50 p-3">
                {isBulk && <p className="text-sm font-medium mb-1.5">{plan.name}</p>}
                <ul className="space-y-1">
                  {plan.changedColumns.map((col) => (
                    <li
                      key={col}
                      className="text-xs text-muted-foreground flex flex-wrap items-center gap-1.5"
                    >
                      <span className="text-foreground">{COLUMN_LABELS[col]}:</span>
                      <span className="line-through">{formatValue(plan.current[col])}</span>
                      <span>→</span>
                      <span className="text-primary font-medium">
                        {formatValue(plan.next[col])}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={onCancel} disabled={applying && !nothingToDo}>
            {nothingToDo ? "Close" : "Cancel"}
          </Button>
          {!nothingToDo && (
            <Button onClick={onConfirm} disabled={applying} className="gap-1.5">
              {applying ? (
                <>
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  Applying…
                </>
              ) : (
                "Apply changes"
              )}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
